import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../components/button';
import {
  Alert,
  Box,
  Card,
  EmptyState,
  LoadingBlock,
  PageContainer,
  PageHeader,
} from '../../components/ui';
import { faCar } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { ProductType } from '../../../__generated__/graphql';
import { PRODUCT_TYPE_OPTIONS } from '../../constants/productTypeOptions';
import { useGetMyCarsV2Query } from '../../services/carService/getMyCarsV2';
import { HostCarCard } from './components/HostCarCard';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../store/authStore';
import { Role, roleStore } from '../../../store/roleStore';
import { getSiteConfig } from '../../constants/siteConfig';
import { useHostAccountGuard } from '../../hooks/useHostAccountGuard';
import { useBookingsOpen } from '../../hooks/useBookingsOpen';
import { getBrand } from '../../constants/brands';
import {
  buildPageWindow,
  pickMatchingPageData,
  resolveValidPage,
  showingRange,
} from '../../utils/hostListingsPagination';

const PAGE_SIZE = 20;

const TitleRow = styled.div`
  ${tw`relative`}
`;

const TypeMenu = styled.div`
  ${tw`absolute right-0 mt-2 bg-white shadow-menu rounded-lg border border-border-default min-w-[180px] z-20 overflow-hidden`}
`;

const TypeOptionButton = styled.button`
  ${tw`w-full text-left px-4 py-2 text-sm`}

  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const CarsGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 w-full`}
`;

const ErrorContainer = styled.div`
  ${tw`w-full text-center text-red-600 mt-10 text-sm`}
`;

const PagerRow = styled.div`
  ${tw`flex flex-col sm:flex-row items-center justify-between gap-3 w-full mt-8`}
`;

const PagerMeta = styled.p`
  ${tw`text-sm text-text-secondary m-0`}
`;

const PagerControls = styled.nav`
  ${tw`flex flex-wrap items-center justify-center gap-1`}
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  ${tw`min-w-[2.25rem] h-9 px-2 rounded-md border text-sm font-medium cursor-pointer`}
  ${({ $active }) =>
    $active
      ? tw`bg-black text-white border-black`
      : tw`bg-white text-text-primary border-border-strong hover:bg-bg-secondary`}
  &:disabled {
    ${tw`opacity-40 cursor-not-allowed`}
  }
`;

export default function HostCarPage() {
  const navigate = useNavigate();
  const titleRef = useRef<HTMLSpanElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const { isAuthenticated } = useSnapshot(authStore);
  const { role } = useSnapshot(roleStore);
  // D41: in host mode a paused (or no longer approved) host sees the cars greyed out,
  // and every action re-checks with the server first. Admin mode is not checked.
  const {
    status: accountStatus,
    notice: accountNotice,
    refresh: refreshHostAccount,
    guard,
  } = useHostAccountGuard();
  const inHostMode = role === Role.HOST;
  const hostInactive = inHostMode && accountStatus !== 'active';
  const hostAccountNotice = hostInactive ? accountNotice : null;
  const guardHostAction = (action: () => void) => (inHostMode ? guard(action) : action());
  // Spec 023: a host on a site that is not taking bookings yet learns why none come in.
  const { open: bookingsOpen } = useBookingsOpen();

  const pageParam = Number(searchParams.get('page') || '1');
  const page = Number.isFinite(pageParam) && pageParam >= 1 ? pageParam : 1;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && inHostMode) void refreshHostAccount();
  }, [isAuthenticated, inHostMode, refreshHostAccount]);

  const { data, loading, error, previousData, refetch } = useGetMyCarsV2Query({
    skip: !isAuthenticated,
    variables: { input: { page, limit: PAGE_SIZE } },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Never show page N's cars under page M's URL while the next page loads.
  const paged = pickMatchingPageData(page, data?.getMyCarsV2, previousData?.getMyCarsV2);
  const metaFallback = previousData?.getMyCarsV2 ?? data?.getMyCarsV2;
  const cars = paged?.items ?? [];
  const totalCount = paged?.totalCount ?? (loading ? (metaFallback?.totalCount ?? 0) : 0);
  const totalPages = paged?.totalPages ?? (loading ? (metaFallback?.totalPages ?? 0) : 0);
  const showInitialLoader = loading && !paged && !metaFallback;
  const showPageTransition = loading && !paged && !!metaFallback;

  useEffect(() => {
    const response = data?.getMyCarsV2;
    if (loading || !response || response.page !== page) return;
    const next = resolveValidPage(
      page,
      response.totalPages,
      response.items.length,
      response.totalCount,
    );
    if (next !== page) {
      setSearchParams(prev => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set('page', String(next));
        return nextParams;
      });
    }
  }, [loading, data, page, setSearchParams]);

  if (!isAuthenticated) {
    return null;
  }

  const siteConfig = getSiteConfig();
  const filteredTypeOptions = siteConfig.allowedProductTypes
    ? PRODUCT_TYPE_OPTIONS.filter(option => siteConfig.allowedProductTypes!.includes(option.value))
    : PRODUCT_TYPE_OPTIONS;

  const redirectToAddCarPage = () => {
    if (showTypeMenu) {
      setShowTypeMenu(false);
      return;
    }
    void guardHostAction(() => {
      if (siteConfig.isSingleProductSite) {
        navigate(`/cars/host/add?type=${siteConfig.defaultProductType}`);
        return;
      }
      setShowTypeMenu(true);
    });
  };

  const handleTypeSelect = (type: ProductType) => {
    setShowTypeMenu(false);
    void guardHostAction(() => navigate(`/cars/host/add?type=${type}`));
  };

  const handleCarClick = (carId: string) => {
    void guardHostAction(() => navigate(`/cars/host/view/${carId}`));
  };

  const handleMenuClick = (e: React.MouseEvent, _carId: string) => {
    e.stopPropagation();
  };

  const setPage = (nextPage: number) => {
    setSearchParams(prev => {
      const nextParams = new URLSearchParams(prev);
      nextParams.set('page', String(nextPage));
      return nextParams;
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.requestAnimationFrame(() => titleRef.current?.focus());
  };

  const range = showingRange(page, PAGE_SIZE, totalCount);
  const windowPages = buildPageWindow(page, totalPages);

  return (
    <PageContainer width="xl" canvas>
      <PageHeader
        title={
          <span ref={titleRef} tabIndex={-1}>
            My Listings
          </span>
        }
        description="Your cars and their status. Open a listing to edit pricing, photos, locations and more."
        actions={
          <TitleRow>
            <Button
              text={
                siteConfig.isSingleProductSite
                  ? `Add ${siteConfig.siteLabel.charAt(0).toUpperCase() + siteConfig.siteLabel.slice(1)}`
                  : 'Add Item'
              }
              onClick={redirectToAddCarPage}
              dataTestId="add-item-button"
            />
            {showTypeMenu && (
              <TypeMenu>
                {filteredTypeOptions.map(option => (
                  <TypeOptionButton
                    key={option.value}
                    onClick={() => handleTypeSelect(option.value)}
                    data-testid={`add-item-type-${option.value.toLowerCase()}`}
                  >
                    {option.label}
                  </TypeOptionButton>
                ))}
              </TypeMenu>
            )}
          </TitleRow>
        }
      />

      {hostAccountNotice && (
        <Box $mb={6}>
          <Alert tone="warning" title={hostAccountNotice.title} data-testid="host-account-notice">
            {hostAccountNotice.body}
            {hostAccountNotice.supportEmail && (
              <>
                {' Email '}
                <a href={`mailto:${hostAccountNotice.supportEmail}`} className="underline">
                  {hostAccountNotice.supportEmail}
                </a>
                .
              </>
            )}
          </Alert>
        </Box>
      )}

      {!bookingsOpen && (
        <Box $mb={6}>
          <Alert
            tone="info"
            title="Bookings are not open yet"
            data-testid="bookings-not-open-notice"
          >
            {`Bookings on ${getBrand().name} are not open yet. Guests can see your cars but can't book them.`}
          </Alert>
        </Box>
      )}

      {showInitialLoader && <LoadingBlock label="Loading your listings" />}

      {error && !paged && (
        <ErrorContainer role="alert">
          Error loading cars: {error.message}{' '}
          <button type="button" className="underline" onClick={() => void refetch()}>
            Retry
          </button>
        </ErrorContainer>
      )}

      {!showInitialLoader && !error && paged && totalCount === 0 && (
        <Card>
          <EmptyState
            icon={faCar}
            title="No listings yet"
            description="Add your first car to start renting it out."
          />
        </Card>
      )}

      {showPageTransition && (
        <div data-testid="host-listings-page-loading">
          <LoadingBlock label="Loading page" />
        </div>
      )}

      {cars.length > 0 && !showPageTransition && (
        <CarsGrid data-testid="host-listings-grid">
          {cars.map((car: any) => (
            <HostCarCard
              key={car.id}
              car={car}
              pausedLabel={hostInactive ? hostAccountNotice?.badge : undefined}
              onClick={() => handleCarClick(car.id)}
              onMenuClick={e => handleMenuClick(e, car.id)}
            />
          ))}
        </CarsGrid>
      )}

      {(totalPages > 0 || page > 1) && (
        <PagerRow data-testid="host-listings-pager">
          <PagerMeta>
            {!paged
              ? `Loading page ${page}…`
              : totalCount === 0
                ? 'No listings'
                : `Showing ${range.from}–${range.to} of ${totalCount}`}
          </PagerMeta>
          <PagerControls aria-label="Host listings pages">
            <PageBtn
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage(page - 1)}
              data-testid="host-listings-prev"
            >
              Prev
            </PageBtn>
            {windowPages.map((p, idx) =>
              p === 'ellipsis' ? (
                <span key={`e-${idx}`} className="px-1 text-gray-400">
                  …
                </span>
              ) : (
                <PageBtn
                  key={p}
                  type="button"
                  $active={p === page}
                  aria-current={p === page ? 'page' : undefined}
                  disabled={loading}
                  onClick={() => setPage(p)}
                  data-testid={`host-listings-page-${p}`}
                >
                  {p}
                </PageBtn>
              ),
            )}
            <PageBtn
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(page + 1)}
              data-testid="host-listings-next"
            >
              Next
            </PageBtn>
          </PagerControls>
        </PagerRow>
      )}
    </PageContainer>
  );
}
